import * as XLSX from 'xlsx';

import { approvalState, APPROVED, REJECTED } from './employeeApproval';
import { isBankApproved } from './employeeBankApproval';
import { getLocationName, getCostCenter } from './employeeUtils';


const fmt = (v) => (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined')
  ? ''
  : String(v).trim();

const fld = (emp, newKey, oldKey) => fmt(emp[newKey]) || fmt(emp[oldKey]);

const pad2 = (n) => String(n).padStart(2, '0');

function stamp(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
         `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

export function excelDate(v) {
  const raw = fmt(v);
  if (!raw) return '';

  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${pad2(iso[2])}-${pad2(iso[3])}`;

  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmy) return `${dmy[3]}-${pad2(dmy[2])}-${pad2(dmy[1])}`;

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/* ── Decoding stored codes ──────────────────────────────────────────────── */

const GENDER_MAP   = { M: 'Male', F: 'Female' };
const MARITAL_MAP  = { S: 'Single', M: 'Married', D: 'Divorced', W: 'Widowed' };
const RELIGION_MAP = {
  B: 'Buddhism', C: 'Christian', H: 'Hindu', I: 'Islam', O: 'Other',
  1: 'Buddhism', 2: 'Christian', 3: 'Hindu', 4: 'Islam', 5: 'Other',
};
const BLOOD_MAP = {
  1: 'A+', 2: 'A-', 3: 'B+', 4: 'B-', 5: 'AB+', 6: 'AB-', 7: 'O+', 8: 'O-',
  0: 'Group O', 9: 'Group B', 10: 'Group B+', 11: 'Group B-', 12: 'Unknown',
};

const dec = (map, v) => {
  const raw = fmt(v);
  return raw ? (map[raw] || raw) : '';
};

/** "True" / "False" arrive as strings, and "False" is truthy in JavaScript. */
const truthy = (v) => v === true || v === 1 ||
  ['true', '1', 'yes', 'y'].includes(String(v ?? '').trim().toLowerCase());

export function isActive(emp) {
  if (emp.HED_IS_ACTIVE !== undefined && emp.HED_IS_ACTIVE !== null && emp.HED_IS_ACTIVE !== '') {
    return truthy(emp.HED_IS_ACTIVE);
  }
  return !emp.p_inactive_type || emp.p_inactive_type === '' || emp.p_inactive_type === '0';
}

const approvalLabel = (emp) => {
  const state = approvalState(emp);
  if (state === APPROVED) return 'Approved';
  if (state === REJECTED) return 'Rejected';
  return 'Pending';
};

/* ── Nested records ──────────────────────────────────────────────────────── */

const listOf = (emp, ...keys) => {
  for (const k of keys) if (Array.isArray(emp[k])) return emp[k];
  return [];
};

const banksOf     = (e) => listOf(e, 'Banks', '_Banks');
const addressesOf = (e) => listOf(e, 'Addresses', '_Addresses');
const contactsOf  = (e) => listOf(e, 'Contacts', '_Contacts');
const qualsOf     = (e) => listOf(e, 'Qualifications', '_Qualifications', 'Educations', '_Educations');

/** The account marked primary, else the first one on file. */
const primaryBank = (e) => {
  const banks = banksOf(e);
  return banks.find((b) => truthy(b.HEB_IS_PRIMARY)) || banks[0] || {};
};

const addressOfType = (e, type) =>
  addressesOf(e).find((a) => fmt(a.HEA_ADDRESS_TYPE).toUpperCase() === type) || {};

/* ── Region ── */
const REGION_KEYS = {
  province:      ['HEA_PROVINCE_ID', 'HEA_PROVINCE_CODE', 'PROVINCE_CODE'],
  district:      ['HEA_DISTRICT_ID', 'HEA_DISTRICT_CODE', 'DISTRICT_CODE'],
  electorate:    ['HEA_ELECTORATE_ID', 'HEA_ELECTORATE_CODE', 'ELECTORATE_CODE'],
  policeStation: ['HEA_POLICE_STATION_ID', 'HEA_STATION_CODE', 'HPD_STATION_CODE'],
};

const regionCode = (e, which) => {
  const a = addressOfType(e, 'P');
  for (const k of REGION_KEYS[which]) if (fmt(a[k])) return fmt(a[k]);
  return '';
};

/** Looks a code up in one of the ctx lists and returns its label, else the code. */
const regionName = (code, rows, codeKeys, nameKeys) => {
  if (!code) return '';
  const hit = (rows || []).find((r) => codeKeys.some((k) => fmt(r[k]) === String(code)));
  if (!hit) return String(code);
  for (const k of nameKeys) if (fmt(hit[k])) return fmt(hit[k]);
  return String(code);
};

/** "No. 25, Galle Road, Colombo 10350" from whichever lines are filled. */
const addressLine = (a) => {
  const street = [a.HEA_ADDRESS_LINE1, a.HEA_ADDRESS_LINE2, a.HEA_ADDRESS_LINE3]
    .map(fmt).filter(Boolean).join(', ');
  const tail = [fmt(a.HEA_CITY), fmt(a.HEA_POSTAL_CODE)].filter(Boolean).join(' ');
  return [street, tail].filter(Boolean).join(', ');
};


function reportingOfficer(emp, all = []) {
  const id = fmt(emp.HED_REPORTING_EMPLOYEE_ID);
  const onRow = fmt(emp.HED_REPORTING_EMPLOYEE_NAME);
  if (!id) return onRow;

  const match = all.find((r) => fmt(r.HED_EMPLOYEE_ID) === id);
  if (!match) return onRow || id;

  const name = fmt(match.HED_REPORT_NAME) ||
    [fmt(match.HED_FIRST_NAME), fmt(match.HED_LAST_NAME)].filter(Boolean).join(' ') || id;
  const svcNo = fmt(match.HED_SERVICE_NO);
  return svcNo ? `${name} (${svcNo})` : name;
}


export const EMPLOYEE_EXPORT_COLUMNS = [
  // Identification
  { label: 'Service No',        width: 13, get: (e) => fld(e, 'HED_SERVICE_NO', 'p_ced_service_no') },
  { label: 'Employee ID',       width: 12, get: (e) => fmt(e.HED_EMPLOYEE_ID) },
  { label: 'EPF No',            width: 12, get: (e) => fld(e, 'HED_EPF_NO', 'p_epf') },
  { label: 'ETF No',            width: 12, get: (e) => fmt(e.HED_ETF_NO) },
  { label: 'TIN No',            width: 14, get: (e) => fld(e, 'HED_TIN_NO', 'p_TinNo') },
  { label: 'NIC No',            width: 16, get: (e) => fld(e, 'HED_NIC_NO', 'p_nid') },
  { label: 'Passport No',       width: 14, get: (e) => fld(e, 'HED_PASSPORT_NO', 'p_passport') },

  // Name
  { label: 'Title',             width: 8,  get: (e) => fld(e, 'HED_TITLE', 'p_title') },
  { label: 'First Name',        width: 18, get: (e) => fld(e, 'HED_FIRST_NAME', 'p_first_name') },
  { label: 'Middle Name',       width: 18, get: (e) => fmt(e.HED_MIDDLE_NAME) },
  { label: 'Last Name',         width: 18, get: (e) => fld(e, 'HED_LAST_NAME', 'p_last_name') },
  { label: 'Initials',          width: 12, get: (e) => fld(e, 'HED_INITIALS', 'p_initials') },
  { label: 'Report Name',       width: 28, get: (e) => fld(e, 'HED_REPORT_NAME', 'p_report_name') },

  // Personal
  { label: 'Gender',            width: 10, get: (e) => dec(GENDER_MAP, fld(e, 'HED_GENDER', 'p_sex')) },
  { label: 'Date of Birth',     width: 13, get: (e) => excelDate(fld(e, 'HED_DATE_OF_BIRTH', 'p_birth_date')) },
  { label: 'Marital Status',    width: 14, get: (e) => dec(MARITAL_MAP, fld(e, 'HED_MARITAL_STATUS', 'p_marital_status')) },
  { label: 'Religion',          width: 12, get: (e) => dec(RELIGION_MAP, fld(e, 'HED_RELIGION', 'p_religion')) },
  { label: 'Blood Group',       width: 12, get: (e) => dec(BLOOD_MAP, fld(e, 'HED_BLOOD_GROUP', 'p_blood_group')) },
  { label: 'Nationality',       width: 12, get: (e) => fmt(e.HED_NATIONALITY) },

  // Contact
  { label: 'Mobile No',         width: 15, get: (e) => fld(e, 'HED_MOBILE_NO', 'p_mobile') },
  { label: 'Home No',           width: 15, get: (e) => fld(e, 'HED_HOME_NO', 'p_home_tel') },
  { label: 'Office No',         width: 15, get: (e) => fld(e, 'HED_OFFICE_NO', 'p_office_tel') },
  { label: 'Personal Email',    width: 28, get: (e) => fld(e, 'HED_PERSONAL_EMAIL', 'p_personal_email') },
  { label: 'Company Email',     width: 28, get: (e) => fld(e, 'HED_COMPANY_EMAIL', 'p_email_address') },

  // Addresses — as one line each; the Addresses sheet has them split up
  { label: 'Permanent Address', width: 40, get: (e) => addressLine(addressOfType(e, 'P')) },
  { label: 'Current Address',   width: 40, get: (e) => addressLine(addressOfType(e, 'C')) },
  { label: 'Distance (km)',     width: 13, get: (e) => fld(e, 'HED_DISTANCE', 'p_Distance') },

  // Region — from the permanent address
  { label: 'Province',       width: 18, get: (e, ctx) =>
      regionName(regionCode(e, 'province'), ctx && ctx.provinces,
        ['PROVINCE_CODE', 'ProvinceCode', 'provinceCode'],
        ['PROVINCE_NAME', 'ProvinceName', 'provinceName']) },
  { label: 'District',       width: 18, get: (e, ctx) =>
      regionName(regionCode(e, 'district'), ctx && ctx.districts,
        ['DISTRICT_CODE', 'DistrictCode', 'districtCode'],
        ['DISTRICT_NAME', 'DistrictName', 'districtName']) },
  { label: 'Electorate',     width: 20, get: (e, ctx) =>
      regionName(regionCode(e, 'electorate'), ctx && ctx.electorates,
        ['ELECTORATE_CODE', 'ElectorateCode', 'electorateCode'],
        ['ELECTORATE_AREA', 'ElectorateArea', 'electorateArea']) },
  { label: 'Police Station', width: 22, get: (e, ctx) =>
      regionName(regionCode(e, 'policeStation'), ctx && ctx.policeStations,
        ['HPD_STATION_CODE', 'StationCode', 'stationCode'],
        ['HPD_POLICESTATION_NAME', 'PoliceStationName', 'policeStationName']) },

  // Employment
  { label: 'Designation',       width: 24, get: (e) => fld(e, 'HED_DESIGNATION_NAME', 'p_designation') },
  { label: 'Department',        width: 24, get: (e) => fld(e, 'HDD_DEPARTMENT_NAME', 'p_department_code') },
  { label: 'Employment Category', width: 18, get: (e) => fld(e, 'HEC_CATEGORY_NAME', 'p_cat_code') },
  { label: 'Grade',             width: 16, get: (e) => fmt(e.HGD_GRADE_NAME) },
  { label: 'Grade Code',        width: 12, get: (e) => fmt(e.HGD_GRADE_CODE) },
  // Two separate facts, so two columns — the old single column showed the
  // cost centre under a heading that promised both.
  { label: 'Location',          width: 22, get: (e) => getLocationName(e) },
  { label: 'Cost Center',       width: 18, get: (e) => getCostCenter(e) },
  { label: 'Group',             width: 14, get: (e) => fmt(e.HED_GROUP) },
  { label: 'Reporting Officer', width: 26, get: (e, ctx) => reportingOfficer(e, ctx.allEmployees) },
  { label: 'Shift',             width: 16, get: (e) => fmt(e.HSD_SHIFT_NAME) },
  { label: 'Shift Start',       width: 11, get: (e) => fmt(e.HSD_START_TIME) },
  { label: 'Shift End',         width: 11, get: (e) => fmt(e.HSD_END_TIME) },
  { label: 'Join Date',         width: 13, get: (e) => excelDate(fld(e, 'HED_DATE_OF_JOINING', 'p_recruitment_date')) },
  { label: 'Confirmation Date', width: 15, get: (e) => excelDate(fld(e, 'HED_CONFIRMATION_DATE', 'p_permanent_date')) },
  { label: 'Total Experience',  width: 14, get: (e) => fmt(e.HED_TOTAL_EXPERIANCE || e.HED_TOTAL_EXPERIENCE) },
  { label: 'Notice Period (days)', width: 16, get: (e) => fmt(e.HED_NOTICE_PERIOD_DAYS) },
  { label: 'OT Rate',           width: 11, get: (e) => fmt(e.HED_OT_RATE) },

  // Status
  { label: 'Status',            width: 10, get: (e) => (isActive(e) ? 'Active' : 'Inactive') },
  { label: 'Leaving Date',      width: 13, get: (e) => (isActive(e) ? '' : excelDate(e.HED_LEAVING_DATE)) },
  { label: 'Leaving Reason',    width: 22, get: (e) => (isActive(e) ? '' : fmt(e.HED_LEAVING_REASON)) },
  { label: 'Approval Status',   width: 14, get: (e) => approvalLabel(e) },
  { label: 'Approved By',       width: 14, get: (e) => fmt(e.HED_APPROVED_BY) },
  { label: 'Approved Date',     width: 14, get: (e) => excelDate(e.HED_APPROVED_DATE) },
  { label: 'System User',       width: 12, get: (e) => (truthy(e.HED_IS_SYSTEM_USER) ? 'Yes' : 'No') },

  // Primary bank — every account is on the Bank Accounts sheet
  { label: 'Bank',              width: 22, get: (e) => fmt(primaryBank(e).HBD_BANK_NAME) },
  { label: 'Bank Branch',       width: 22, get: (e) => fmt(primaryBank(e).HBB_BRANCH_NAME) },
  { label: 'Account Number',    width: 20, get: (e) => fmt(primaryBank(e).HEB_ACCOUNT_NUMBER) },
  { label: 'Account Name',      width: 20, get: (e) => fmt(primaryBank(e).HEB_ACCOUNT_NAME) },
  { label: 'Account Type',      width: 13, get: (e) => fmt(primaryBank(e).HEB_ACCOUNT_TYPE) },
  { label: 'Bank Approved',     width: 13, get: (e) => {
    const banks = banksOf(e);
    if (!banks.length) return '';
    return banks.every(isBankApproved) ? 'Yes' : 'No';
  } },
  { label: 'Bank Accounts',     width: 13, get: (e) => String(banksOf(e).length) },

  // First emergency contact — every contact is on its own sheet
  { label: 'Emergency Contact', width: 22, get: (e) => fmt((contactsOf(e)[0] || {}).HEC_CONTACT_NAME) },
  { label: 'Emergency Relationship', width: 18, get: (e) => fmt((contactsOf(e)[0] || {}).HEC_RELATIONSHIP) },
  { label: 'Emergency Contact No', width: 18, get: (e) => fmt((contactsOf(e)[0] || {}).HEC_CONTACT_NUMBER) },

  { label: 'Qualifications',    width: 13, get: (e) => String(qualsOf(e).length) },
  { label: 'Remarks',           width: 30, get: (e) => fld(e, 'HED_REMARKS', 'p_remarks') },
];


const OWNER_COLUMNS = [
  { label: 'Service No', width: 13, get: (e) => fld(e, 'HED_SERVICE_NO', 'p_ced_service_no') },
  { label: 'Employee',   width: 26, get: (e) => fld(e, 'HED_REPORT_NAME', 'p_report_name') ||
      [fmt(e.HED_FIRST_NAME), fmt(e.HED_LAST_NAME)].filter(Boolean).join(' ') },
];

const DETAIL_SHEETS = [
  {
    name: 'Bank Accounts',
    rowsOf: banksOf,
    columns: [
      { label: 'Bank',           width: 22, get: (b) => fmt(b.HBD_BANK_NAME) },
      { label: 'Branch',         width: 22, get: (b) => fmt(b.HBB_BRANCH_NAME) },
      { label: 'Account Number', width: 20, get: (b) => fmt(b.HEB_ACCOUNT_NUMBER) },
      { label: 'Account Name',   width: 22, get: (b) => fmt(b.HEB_ACCOUNT_NAME) },
      { label: 'Account Type',   width: 13, get: (b) => fmt(b.HEB_ACCOUNT_TYPE) },
      { label: 'Primary',        width: 9,  get: (b) => (truthy(b.HEB_IS_PRIMARY) ? 'Yes' : 'No') },
      { label: 'Approved',       width: 10, get: (b) => (isBankApproved(b) ? 'Yes' : 'No') },
    ],
  },
  {
    name: 'Addresses',
    rowsOf: addressesOf,
    columns: [
      { label: 'Type', width: 12, get: (a) => {
        const t = fmt(a.HEA_ADDRESS_TYPE).toUpperCase();
        if (t === 'P') return 'Permanent';
        if (t === 'C') return 'Current';
        return t;
      } },
      { label: 'Line 1',      width: 24, get: (a) => fmt(a.HEA_ADDRESS_LINE1) },
      { label: 'Line 2',      width: 24, get: (a) => fmt(a.HEA_ADDRESS_LINE2) },
      { label: 'Line 3',      width: 24, get: (a) => fmt(a.HEA_ADDRESS_LINE3) },
      { label: 'City',        width: 18, get: (a) => fmt(a.HEA_CITY) },
      { label: 'Postal Code', width: 12, get: (a) => fmt(a.HEA_POSTAL_CODE) },
    ],
  },
  {
    name: 'Emergency Contacts',
    rowsOf: contactsOf,
    columns: [
      { label: 'Name',         width: 24, get: (c) => fmt(c.HEC_CONTACT_NAME) },
      { label: 'Relationship', width: 16, get: (c) => fmt(c.HEC_RELATIONSHIP) },
      { label: 'Contact No',   width: 16, get: (c) => fmt(c.HEC_CONTACT_NUMBER) },
      { label: 'Type',         width: 14, get: (c) => fmt(c.HEC_CONTACT_TYPE) },
      { label: 'Address',      width: 34, get: (c) => fmt(c.HEC_ADDRESS) },
    ],
  },
  {
    name: 'Qualifications',
    rowsOf: qualsOf,
    columns: [
      { label: 'Qualification',  width: 28, get: (q) => fmt(q.HEQ_QUALIFICATION_NAME) },
      { label: 'Field of Study', width: 24, get: (q) => fmt(q.HEQ_FIELD_OF_STUDY) },
      { label: 'Institute',      width: 28, get: (q) => fmt(q.HEQ_INSTITUTE) },
      { label: 'Result',         width: 14, get: (q) => fmt(q.HEQ_RESULT) },
      { label: 'Grade',          width: 12, get: (q) => fmt(q.HEQ_GRADE) },
      { label: 'GPA',            width: 10, get: (q) => fmt(q.HEQ_GPA) },
      { label: 'Start Date',     width: 13, get: (q) => excelDate(q.HEQ_START_DATE) },
      { label: 'Completed Date', width: 15, get: (q) => excelDate(q.HEQ_COMPLETED_DATE) },
      { label: 'Certificate No', width: 16, get: (q) => fmt(q.HEQ_CERTIFICATE_NO) },
    ],
  },
];

function buildDetailSheet(spec, employees) {
  const columns = [...OWNER_COLUMNS, ...spec.columns];
  const body = [];

  employees.forEach((emp) => {
    spec.rowsOf(emp).forEach((child) => {
      body.push([
        ...OWNER_COLUMNS.map((c) => c.get(emp)),
        ...spec.columns.map((c) => c.get(child, emp)),
      ]);
    });
  });

  // An empty sheet still gets its header row, so the file's shape never varies.
  const ws = XLSX.utils.aoa_to_sheet([columns.map((c) => c.label), ...body]);
  ws['!cols'] = columns.map((c) => ({ wch: c.width || 14 }));
  if (body.length) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: body.length, c: columns.length - 1 },
      }),
    };
  }
  return ws;
}


export function exportEmployeesToExcel({
  employees = [], allEmployees = null, filters = {},
  // Region master lists are optional: pass them to print names, omit them
  // and the columns fall back to the raw codes.
  provinces = [], districts = [], electorates = [], policeStations = [],
} = {}) {
  const ctx = {
    allEmployees: (allEmployees && allEmployees.length) ? allEmployees : employees,
    // Optional: pass region master lists to print names instead of codes.
    provinces, districts, electorates, policeStations,
  };

  const activeFilters =
    Object.entries(filters)
      .filter(([, v]) => v && v !== 'All')
      .map(([k, v]) => `${k}: ${v}`)
      .join('   |   ') || 'None';

  const activeCount = employees.filter(isActive).length;

  const headerRows = [
    ['EMPLOYEE DETAILS'],
    [`Filters: ${activeFilters}`],
    [`Generated: ${stamp()}`],
    [`Employees: ${employees.length}   (Active: ${activeCount}   Inactive: ${employees.length - activeCount})`],
    [],
  ];

  const columnHeader = EMPLOYEE_EXPORT_COLUMNS.map((c) => c.label);
  const bodyRows = employees.map((emp) => EMPLOYEE_EXPORT_COLUMNS.map((c) => c.get(emp, ctx)));

  const totalsRow = EMPLOYEE_EXPORT_COLUMNS.map((c, i) =>
    (i === 0 ? `TOTAL — ${employees.length} employee${employees.length === 1 ? '' : 's'}` : ''));

  const aoa = [...headerRows, columnHeader, ...bodyRows, [], totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = EMPLOYEE_EXPORT_COLUMNS.map((c) => ({ wch: c.width || 14 }));

  const lastCol = EMPLOYEE_EXPORT_COLUMNS.length - 1;
  ws['!merges'] = headerRows
    .slice(0, 4)
    .map((_, i) => ({ s: { r: i, c: 0 }, e: { r: i, c: lastCol } }));


  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRows.length, c: 0 },
      e: { r: headerRows.length + bodyRows.length, c: lastCol },
    }),
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Employees');

  DETAIL_SHEETS.forEach((spec) => {
    XLSX.utils.book_append_sheet(wb, buildDetailSheet(spec, employees), spec.name);
  });

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const d = new Date();
  const filename = `Employee_Details_${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}.xlsx`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return filename;
}